import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import alertRepository from '../services/repositories/alertRepository';
import authService from '../services/auth';

const AlertsContext = createContext();

export const useAlerts = () => {
  const context = useContext(AlertsContext);
  if (!context) {
    throw new Error('useAlerts debe ser usado dentro de AlertsProvider');
  }
  return context;
};

/* Las alertas leídas se guardan por cuenta y sobreviven a la recarga: marcar
   como leída y encontrárselas otra vez al volver es lo que hacía inútil el
   badge. La clave incluye el correo para que dos sesiones distintas en el mismo
   navegador no compartan el estado de lectura. */
const claveLeidas = () => {
  const quien = authService.isDemo()
    ? 'demo'
    : localStorage.getItem('userEmail') || 'anon';
  return `nissaguard.alerts.read.${quien}`;
};

function leerLeidas() {
  try {
    const crudo = localStorage.getItem(claveLeidas());
    return new Set(crudo ? JSON.parse(crudo) : []);
  } catch {
    return new Set();
  }
}

const REFRESH_MS = 60000;

/**
 * Alertas de la cuenta.
 *
 * Antes este proveedor no consultaba nada: llevaba cinco alertas escritas a
 * mano —presión baja, consumo elevado, flujo continuo— y se las servía a TODAS
 * las cuentas. El "5" del menú lateral no era el número de incidencias de la
 * instalación de nadie: era la longitud de un array del código. Una cuenta real
 * sin dispositivos veía cinco avisos sobre una tubería que no existe.
 *
 * Ahora salen del repositorio, que decide la fuente según la sesión, igual que
 * dispositivos y telemetría.
 */
export const AlertsProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readAlerts, setReadAlerts] = useState(() => leerLeidas());

  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      setAlerts(await alertRepository.list());
    } catch (err) {
      console.error('Error cargando alertas:', err);
      setError(err.message || 'No se pudieron cargar las alertas');
      // Sin datos se muestra la lista vacía, nunca alertas de ejemplo: un aviso
      // inventado sobre una instalación real es peor que ningún aviso.
      setAlerts([]);
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, []);

  /* Atado a la sesión, no al montaje: este proveedor vive por encima del router
     y se monta antes del login, así que consultar la autenticación una sola vez
     al montar dejaría la lista vacía para siempre. Ver DeviceContext. */
  useEffect(() => {
    let intervalo = null;
    const detener = () => {
      if (intervalo) { clearInterval(intervalo); intervalo = null; }
    };

    const sincronizar = (estaAutenticado) => {
      detener();
      if (!estaAutenticado) {
        setAlerts([]);
        setLoading(false);
        return;
      }
      setReadAlerts(leerLeidas());   // el estado de lectura es de quien entra
      cargar();
      intervalo = setInterval(() => cargar(true), REFRESH_MS);
    };

    sincronizar(authService.isAuthenticated());
    const desuscribir = authService.onSessionChange(sincronizar);
    return () => { desuscribir(); detener(); };
  }, [cargar]);

  const guardarLeidas = useCallback((conjunto) => {
    setReadAlerts(conjunto);
    try {
      localStorage.setItem(claveLeidas(), JSON.stringify([...conjunto]));
    } catch (err) {
      console.warn('No se pudo guardar el estado de lectura:', err.message);
    }
  }, []);

  const handleMarkAsRead = useCallback(
    (alertId) => guardarLeidas(new Set([...readAlerts, alertId])),
    [readAlerts, guardarLeidas]
  );

  const handleMarkAllAsRead = useCallback(
    () => guardarLeidas(new Set(alerts.map((a) => a.id))),
    [alerts, guardarLeidas]
  );

  /* Resolver va al backend y luego se recarga: la lista la manda el servidor,
     no el navegador. Antes esto era un setState y la alerta reaparecia al
     recargar la pagina. */
  const resolveAlert = useCallback(
    async (deviceId, alertId) => {
      await alertRepository.resolve(deviceId, alertId);
      await cargar(true);
    },
    [cargar]
  );

  const value = useMemo(() => {
    /* "Nueva" es una propiedad de la alerta (menos de 24 h), no una lista
       aparte. Antes eran dos arrays fijos y una alerta jamás dejaba de ser
       nueva por mucho tiempo que pasara. */
    /* Activa vs resuelta: lo dice el backend con `resolved`, no la pantalla. */
    const activas = alerts.filter((a) => !a.resolved);
    const resueltas = alerts.filter((a) => a.resolved);

    const nuevas = alerts.filter((a) => a.isNew);
    const antiguas = alerts.filter((a) => !a.isNew);
    const sinLeer = (lista) => lista.filter((a) => !readAlerts.has(a.id));
    const leidas = (lista) => lista.filter((a) => readAlerts.has(a.id));

    const unreadNewAlerts = sinLeer(nuevas);
    const unreadOldAlerts = sinLeer(antiguas);

    return {
      newAlerts: nuevas,
      oldAlerts: antiguas,
      readAlerts,
      unreadNewAlerts,
      unreadOldAlerts,
      readNewAlerts: leidas(nuevas),
      readOldAlerts: leidas(antiguas),
      /* El badge cuenta lo que sigue ABIERTO, no lo que no se ha mirado. Antes
         contaba alertas "no leídas" de una lista fija, asi que marcaba 5 en
         cuentas sin un solo dispositivo. */
      totalUnreadAlerts: activas.length,
      activeAlerts: activas,
      resolvedAlerts: resueltas,
      loading,
      error,
      refreshAlerts: cargar,
      resolveAlert,
      handleMarkAsRead,
      handleMarkAllAsRead,
    };
  }, [alerts, readAlerts, loading, error, cargar, resolveAlert, handleMarkAsRead, handleMarkAllAsRead]);

  return (
    <AlertsContext.Provider value={value}>
      {children}
    </AlertsContext.Provider>
  );
};
